import {
  type AiAccountCrossEnterpriseResponse,
  aiAccountCrossEnterpriseResponseSchema,
  type EnterpriseJoinRequiredResponse,
  enterpriseJoinRequiredResponseSchema,
} from "@mg/schemas/src/christo/catalyst";
import { Box, Grid, Link, Text } from "@radix-ui/themes";
import {
  type ErrorComponentProps,
  type ErrorRouteComponent,
  // eslint-disable-next-line import/named
  Link as NavLink,
  useLocation,
  useParams,
} from "@tanstack/react-router";
import cx from "classnames";
import {
  useEffect,
  useMemo,
  useState,
  type FC,
  type PropsWithChildren,
} from "react";
import { useTranslation } from "react-i18next";

import { getProject } from "../../../../../services/projects";
import { isEnterpriseMfaStepParkedError } from "../../../../../utils/enterpriseMfaStep";
import { useAppSelector } from "../../../../../utils/hooks";
import { useEnterprises } from "../../../../../utils/queries/enterprises";
import {
  captureError,
  ErrorAction,
} from "../../../../root/customErrorComponent";
import { EnterpriseMfaStepPending } from "../../../components/EnterpriseMfaStepPending";
import { ticketsRoute } from "../../../route";

type ErrorProps = ErrorComponentProps & {
  error: Error | Response;
};

type ShareLinkErrorBody =
  | AiAccountCrossEnterpriseResponse
  | EnterpriseJoinRequiredResponse;

/** Current workspace label from the session — never from the share-link API. */
function useCurrentWorkspaceName(): string {
  const user = useAppSelector((state) => state.auth.value);
  const { data: enterprises } = useEnterprises();
  const current = enterprises?.find((e) => e._id === user?.enterpriseId);
  return (
    current?.name ??
    user?.enterpriseName ??
    user?.company ??
    "your current workspace"
  );
}

const ErrorComponent: ErrorRouteComponent = (props: ErrorProps) => {
  const { error } = props;
  const location = useLocation();
  const params = useParams({ strict: false }) as { ticketId?: string };
  const componentType = useMemo(() => {
    if (location.pathname.includes("knowledge-database")) {
      return "knowledge-database";
    }
    return "ticket";
  }, [location.pathname]);

  const [shareLinkBody, setShareLinkBody] = useState<ShareLinkErrorBody | null>(
    null,
  );
  const [shareLinkBodyReady, setShareLinkBodyReady] = useState(false);

  useEffect(() => {
    captureError(`${componentType || "ticket"}_error`, error);
  }, []); // Runs only once on mount

  useEffect(() => {
    let cancelled = false;

    async function loadShareLinkBody() {
      if (!(error instanceof Response)) {
        setShareLinkBodyReady(true);
        return;
      }
      if (error.status !== 403 && error.status !== 409) {
        setShareLinkBodyReady(true);
        return;
      }
      try {
        const json: unknown = await error.clone().json();
        if (cancelled) return;
        const aiParsed = aiAccountCrossEnterpriseResponseSchema.safeParse(json);
        if (aiParsed.success) {
          setShareLinkBody(aiParsed.data);
          setShareLinkBodyReady(true);
          return;
        }
        const joinParsed = enterpriseJoinRequiredResponseSchema.safeParse(json);
        if (joinParsed.success) {
          setShareLinkBody(joinParsed.data);
          setShareLinkBodyReady(true);
          return;
        }
      } catch {
        // Fall through to default status handling.
      }
      if (!cancelled) {
        setShareLinkBodyReady(true);
      }
    }

    void loadShareLinkBody();
    return () => {
      cancelled = true;
    };
  }, [error]);

  function renderError() {
    if (shareLinkBody?.code === "ai_account_cross_enterprise") {
      return <AiAccountBlocked body={shareLinkBody} />;
    }
    if (shareLinkBody?.code === "enterprise_join_required") {
      return (
        <JoinEnterpriseConfirm
          body={shareLinkBody}
          ticketId={params.ticketId}
        />
      );
    }

    // The share-link auto-switch answered this ticket/folder read with the TOTP
    // step. `services/projects` parked it — `AuthenticationDialog` is already
    // open on it — and raised this instead of letting the ticket schema report
    // a parse failure on a body that was never a ticket. Say what is happening
    // rather than showing the generic error page under the open dialog: the
    // dialog is dismissed by a cancel, and this is what the user is left with.
    if (isEnterpriseMfaStepParkedError(error)) {
      return <EnterpriseMfaStepPending />;
    }

    // Wait for JSON parse before treating 403/409 as generic errors.
    if (
      error instanceof Response &&
      (error.status === 403 || error.status === 409) &&
      !shareLinkBodyReady
    ) {
      return null;
    }

    if ("status" in error) {
      switch (error.status) {
        case 404:
        case 500:
          return <NotFound componentType={componentType} />;
        case 401:
          return <Unauthorized />;
        default:
          return <DefaultError />;
      }
    }
    // Any non-`Response` error (e.g. a `ZodError` thrown by `parseResponse`
    // when the ticket payload doesn't match `getTicketResponseSchema`) falls
    // through here. Previously this returned `undefined`, which TanStack
    // Router happily rendered as an empty React subtree — the user just saw
    // a blank canvas with no clue anything was wrong. Always render
    // *something* so we don't silently white-screen on schema drift.
    return <DefaultError />;
  }

  return renderError();
};

export default ErrorComponent;

function getNotFoundMessages(componentType?: string): {
  title: string;
  description: string;
} {
  switch (componentType) {
    case "knowledge-database":
      return {
        title: "These Records Not Found",
        description: "The records you are trying to access do not exist.",
      };
    default:
      return {
        title: "Ticket Not Found",
        description: "The ticket you are trying to access does not exist.",
      };
  }
}

function NotFound({ componentType }: { componentType?: string }) {
  const { t } = useTranslation("ui");
  const { title, description } = getNotFoundMessages(componentType);

  return (
    <ErrorContainer>
      <h1 className="p-5 text-3xl">{title}</h1>
      <Text weight="medium">{description}</Text>
      <Text>
        {t("tickets.errorComponent.perhapsLinkWasAccidentallyChanged")}
      </Text>
      <ErrorAction />
    </ErrorContainer>
  );
}

function Unauthorized() {
  const { t } = useTranslation("ui");
  return (
    <ErrorContainer>
      <Text weight="medium">
        {t("tickets.errorComponent.doNotHaveAccessTicket")}
      </Text>
      <br />
      <Text>
        Please return to the{" "}
        <Link asChild>
          <NavLink to={ticketsRoute.to} className="puntt-link">
            {t("tickets.errorComponent.ticketsPage")}
          </NavLink>
        </Link>
        .
      </Text>
      <ErrorAction withButtons={false} />
    </ErrorContainer>
  );
}

function AiAccountBlocked({
  body,
}: {
  body: AiAccountCrossEnterpriseResponse;
}) {
  const { t } = useTranslation("ui");
  const currentName = useCurrentWorkspaceName();
  const targetName = body.targetEnterpriseName;

  return (
    <ErrorContainer>
      <div
        role="alert"
        data-testid="share-link-ai-blocked"
        className="mx-auto max-w-xl space-y-3"
      >
        <h1 className="p-5 text-3xl">
          {t("tickets.errorComponent.aiAccountCannotOpenLink")}
        </h1>
        <Text weight="medium">
          You are signed in with the AI account for <em>{currentName}</em>
          {targetName ? (
            <>
              , but this share link belongs to another workspace (
              <em>{targetName}</em>).
            </>
          ) : (
            <>, but this share link belongs to another workspace.</>
          )}
        </Text>
        <Text>
          AI accounts are workspace-scoped and must not follow links into other
          enterprises. That keeps tenant data from mixing. Sign in with a normal
          user account
          {targetName ? (
            <>
              , or the AI account for <em>{targetName}</em>,
            </>
          ) : (
            <> for the correct workspace,</>
          )}{" "}
          before opening this link.
        </Text>
        <Text>
          Return to the{" "}
          <Link asChild>
            <NavLink to={ticketsRoute.to} className="puntt-link">
              {t("tickets.errorComponent.ticketsPage")}
            </NavLink>
          </Link>
          .
        </Text>
        <ErrorAction withButtons={false} />
      </div>
    </ErrorContainer>
  );
}

function JoinEnterpriseConfirm({
  body,
  ticketId,
}: {
  body: EnterpriseJoinRequiredResponse;
  ticketId?: string;
}) {
  const { t } = useTranslation("ui");
  const [pending, setPending] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const currentName = useCurrentWorkspaceName();
  const targetName = body.targetEnterpriseName;
  const resourceLabel = body.resource === "folder" ? "folder" : "ticket";

  const onContinue = async () => {
    if (!ticketId) {
      setJoinError(
        "Could not continue. Return to tickets and try the link again.",
      );
      return;
    }
    setPending(true);
    setJoinError(null);
    try {
      await getProject(ticketId, { confirmJoin: true });
      // Member/join success usually reloads via X-Refreshed-Token; if not, reload.
      window.location.reload();
    } catch {
      setPending(false);
      setJoinError(
        "Could not join that workspace. Try again or cancel and stay where you are.",
      );
    }
  };

  return (
    <ErrorContainer>
      <div
        data-testid="share-link-join-confirm"
        className="mx-auto max-w-xl space-y-3"
        aria-labelledby="share-link-join-confirm-title"
      >
        <h1 id="share-link-join-confirm-title" className="p-5 text-3xl">
          {t("tickets.errorComponent.joinAnotherWorkspace")}
        </h1>
        <Text weight="medium">
          You are signed in to <em>{currentName}</em>. This share link opens a{" "}
          {resourceLabel} in <em>{targetName}</em>.
        </Text>
        <Text>
          Continuing will add your account to <em>{targetName}</em> and open the
          shared {resourceLabel}. Cancel to stay in <em>{currentName}</em>.
        </Text>
        {joinError ? (
          <Text color="red" data-testid="share-link-join-error">
            {joinError}
          </Text>
        ) : null}
        <div className="flex flex-wrap items-center justify-center gap-3 pt-4">
          <button
            type="button"
            data-testid="share-link-join-confirm-button"
            disabled={pending}
            onClick={() => void onContinue()}
            className="rt-reset rt-BaseButton rt-r-size-1 rt-variant-solid rt-Button cursor-pointer rounded border-none px-5 py-2.5 text-[14px] transition-colors disabled:cursor-not-allowed disabled:opacity-60"
          >
            {pending
              ? t("tickets.errorComponent.continuing")
              : t("common:actions.continue")}
          </button>
          <Link asChild>
            <NavLink
              to={ticketsRoute.to}
              data-testid="share-link-join-cancel-button"
              className="puntt-link"
            >
              Cancel — stay in {currentName}
            </NavLink>
          </Link>
        </div>
        <ErrorAction withButtons={false} />
      </div>
    </ErrorContainer>
  );
}

function DefaultError() {
  const { t } = useTranslation("ui");
  return (
    <ErrorContainer>
      <h1 className="p-5 text-3xl">
        {t("tickets.errorComponent.unknownErrorOccurred")}
      </h1>
      <ErrorAction />
    </ErrorContainer>
  );
}

/**
 * A simple container component used to wrap error text. Positions the text to
 * the very center of the page.
 */
const ErrorContainer: FC<PropsWithChildren> = ({ children }) => {
  return (
    <Grid
      align="center"
      justify="center"
      height="100vh"
      className={cx("w-full place-content-center p-12 text-center")}
    >
      <Box>{children}</Box>
    </Grid>
  );
};
